/**
 * Premium Product Grid
 * - Desktop: hover reveals arrows, click cycles images (loops)
 * - Mobile: horizontal swipe cycles images, tap still opens product page
 * - Add to cart: AJAX add via /cart/add.js, then refreshes the existing
 *   cart-drawer section and opens it.
 *
 * No external libraries. No frameworks.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 40; // px

  function getLiveRegion() {
    return document.getElementById('PremiumCartLiveRegion');
  }

  function announce(message) {
    var region = getLiveRegion();
    if (region) {
      region.textContent = message;
    }
  }

  function initGallery(card) {
    var isMulti = card.getAttribute('data-multi') === 'true';
    if (!isMulti) return;

    var media = card.querySelector('.premium-card__media');
    var images = Array.prototype.slice.call(card.querySelectorAll('[data-premium-image]'));
    var prevBtn = card.querySelector('[data-premium-prev]');
    var nextBtn = card.querySelector('[data-premium-next]');
    var currentIndex = 0;

    function showImage(index) {
      var total = images.length;
      if (total === 0) return;
      currentIndex = ((index % total) + total) % total; // loop both directions
      images.forEach(function (img, i) {
        if (i === currentIndex) {
          img.classList.add('is-active');
        } else {
          img.classList.remove('is-active');
        }
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        showImage(currentIndex + 1);
      });
    }

    // Touch swipe (mobile). Arrows are hidden on touch devices via CSS.
    var touchStartX = 0;
    var touchStartY = 0;
    var isHorizontalSwipe = false;
    var touchMoved = false;

    media.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        isHorizontalSwipe = false;
        touchMoved = false;
      },
      { passive: true }
    );

    media.addEventListener(
      'touchmove',
      function (e) {
        if (!e.touches || e.touches.length !== 1) return;
        var deltaX = e.touches[0].clientX - touchStartX;
        var deltaY = e.touches[0].clientY - touchStartY;

        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          isHorizontalSwipe = true;
          touchMoved = true;
          // Prevent vertical page scroll only once we know this is a
          // deliberate horizontal swipe on the gallery.
          e.preventDefault();
        }
      },
      { passive: false }
    );

    media.addEventListener(
      'touchend',
      function (e) {
        if (!isHorizontalSwipe) return;
        var touch = e.changedTouches && e.changedTouches[0];
        if (!touch) return;
        var deltaX = touch.clientX - touchStartX;

        if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
          if (deltaX < 0) {
            showImage(currentIndex + 1);
          } else {
            showImage(currentIndex - 1);
          }
        }
        isHorizontalSwipe = false;
      },
      { passive: true }
    );

    // If the gesture was a real swipe, suppress the click that would
    // otherwise navigate to the product page.
    var mediaLink = card.querySelector('.premium-card__media-link');
    if (mediaLink) {
      mediaLink.addEventListener('click', function (e) {
        if (touchMoved) {
          e.preventDefault();
          touchMoved = false;
        }
      });
    }
  }

  function setButtonState(button, state) {
    button.classList.remove('is-loading', 'is-error');
    if (state) {
      button.classList.add(state);
    }
  }

  function refreshCartDrawer() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (response) {
        if (!response.ok) throw new Error('Section refresh failed');
        return response.json();
      })
      .then(function (sections) {
        if (sections['cart-drawer']) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          var newDrawer = doc.querySelector('cart-drawer');
          var oldDrawer = document.querySelector('cart-drawer');
          if (newDrawer && oldDrawer && oldDrawer.parentNode) {
            oldDrawer.parentNode.replaceChild(newDrawer, oldDrawer);
          }
        }
        if (sections['cart-icon-bubble']) {
          var bubbleParser = new DOMParser();
          var bubbleDoc = bubbleParser.parseFromString(sections['cart-icon-bubble'], 'text/html');
          var newBubble = bubbleDoc.querySelector('[id^="cart-icon-bubble"]') || bubbleDoc.body.firstElementChild;
          var oldBubble = document.querySelector('[id^="cart-icon-bubble"]');
          if (newBubble && oldBubble) {
            oldBubble.innerHTML = newBubble.innerHTML;
          }
        }
      })
      .catch(function () {
        // If section refresh fails (e.g. section handle differs in this
        // theme), the item is still in the cart - it just won't visually
        // refresh the drawer. Fail silently here; the button state below
        // still confirms success/failure of the actual add.
      });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    if (drawer) {
      drawer.classList.remove('is-empty');
    }
  }

  function initAddToCart(card) {
    var button = card.querySelector('[data-premium-atc]');
    if (!button) return;

    button.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();

      if (button.classList.contains('is-loading')) return;

      var variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      setButtonState(button, 'is-loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error((data && data.description) || 'Could not add to cart');
            }
            return data;
          });
        })
        .then(function () {
          return refreshCartDrawer();
        })
        .then(function () {
          setButtonState(button, null);
          openCartDrawer();
          announce('Item added to cart');
        })
        .catch(function (error) {
          setButtonState(button, 'is-error');
          announce(error && error.message ? error.message : 'Could not add item to cart');
          setTimeout(function () {
            setButtonState(button, null);
          }, 2000);
        });
    });
  }

  function initAllCards(root) {
    var cards = (root || document).querySelectorAll('[data-premium-card]');
    cards.forEach(function (card) {
      initGallery(card);
      initAddToCart(card);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllCards(document);
    });
  } else {
    initAllCards(document);
  }

  // Re-init when Shopify re-renders the section in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    initAllCards(event.target);
  });
})();
